import { useEffect, useState } from 'react';

interface FontLoadingState {
  isLoaded: boolean;
  isError: boolean;
  progress: number;
}

export const useFontLoading = (): FontLoadingState => {
  const [state, setState] = useState<FontLoadingState>({
    isLoaded: false,
    isError: false,
    progress: 0
  });

  useEffect(() => {
    // Check if fonts are already loaded
    if (document.fonts && document.fonts.check) {
      const checkFonts = () => {
        const interLoaded = document.fonts.check('16px Inter');
        const jetbrainsLoaded = document.fonts.check('16px JetBrains Mono');
        
        if (interLoaded && jetbrainsLoaded) {
          setState({ isLoaded: true, isError: false, progress: 100 });
          document.body.classList.add('font-loaded');
          document.body.classList.remove('font-loading');
          return;
        }
        
        // Calculate progress
        const progress = ((interLoaded ? 50 : 0) + (jetbrainsLoaded ? 50 : 0));
        setState(prev => ({ ...prev, progress }));
      };

      // Initial check
      checkFonts();

      // Listen for font loading events
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          setState({ isLoaded: true, isError: false, progress: 100 });
          document.body.classList.add('font-loaded');
          document.body.classList.remove('font-loading');
        });
      }

      // Fallback timeout
      const timeout = setTimeout(() => {
        if (!state.isLoaded) {
          setState(prev => ({ ...prev, isLoaded: true, progress: 100 }));
          document.body.classList.add('font-loaded');
          document.body.classList.remove('font-loading');
        }
      }, 3000);

      return () => clearTimeout(timeout);
    } else {
      // Fallback for browsers without Font Loading API
      setState({ isLoaded: true, isError: false, progress: 100 });
      document.body.classList.add('font-loaded');
    }
  }, [state.isLoaded]);

  return state;
};

// Hook for preloading specific fonts
export const usePreloadFonts = (fontUrls: string[]) => {
  useEffect(() => {
    fontUrls.forEach(url => {
      // Check if link already exists
      const existingLink = document.querySelector(`link[rel="preload"][href="${url}"]`);
      if (existingLink) return;

      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = url;
      link.as = 'font';
      link.type = 'font/woff2';
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    });
  }, [fontUrls]);
};

// Hook for font display optimization
export const useFontDisplay = () => {
  useEffect(() => {
    // Add font loading class to body
    document.body.classList.add('font-loading');

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('font-loading', 'font-loaded');
    };
  }, []);
};
